import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ndfApi } from '../../services/ndf-api';
import type { Depense } from '../../types/ndf';
import { BoutonPrincipal, BoutonSecondaire, Etiquette, Selection } from '../../components/ndf/ui/Champ';
import {
  erreurGlobale,
  erreursParChamp,
  messageSiAucunChampAffiche,
  type ErreursChamps,
} from '../../components/ndf/ui/erreurs-champs';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { ApercuJustificatif } from '../../components/ndf/ApercuJustificatif';
import {
  CHAMPS_AFFICHES,
  champsDepuis,
  FormulaireDepense,
} from '../../components/ndf/FormulaireDepense';
import { useToast } from '../../context/ToastContext';
import { useNdfMoi } from '../../hooks/useNdfMoi';

/**
 * Détail d'une dépense — consultation, correction, suppression.
 *
 * Même règle que partout : on ouvre en LECTURE, on modifie sur un geste
 * explicite. La suppression n'est proposée que sur un brouillon : une dépense
 * partie en approbation se refuse, elle ne s'efface pas — sinon le circuit
 * perdrait la trace de ce qui a été présenté.
 */

const STATUTS: Record<string, { libelle: string; ton: 'neutre' | 'actif' | 'positif' | 'alerte' | 'attention' }> = {
  brouillon: { libelle: 'Brouillon', ton: 'neutre' },
  a_soumettre: { libelle: 'À soumettre', ton: 'attention' },
  soumise: { libelle: 'En attente d’approbation', ton: 'actif' },
  approuvee_manager: { libelle: 'Approuvée par le manager', ton: 'actif' },
  approuvee_rh: { libelle: 'À rembourser', ton: 'positif' },
  remboursee: { libelle: 'Remboursée', ton: 'positif' },
  rejetee: { libelle: 'Refusée', ton: 'alerte' },
};

const MODIFIABLES = ['brouillon', 'a_soumettre', 'rejetee'];

function euros(v: number) {
  return v.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}
function dateFr(v: string) {
  const [a, m, j] = v.split('-');
  return `${j}/${m}/${a}`;
}

export function NdfDepenseDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const [edition, setEdition] = useState(false);
  const [erreurs, setErreurs] = useState<ErreursChamps>({});
  const [confirmation, setConfirmation] = useState(false);
  const [apercu, setApercu] = useState(false);
  const { peutAdministrer, peutApprouverRh } = useNdfMoi();

  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'depenses'],
    queryFn: () => ndfApi.depenses.liste(),
    retry: false,
  });
  const { data: categoriesData } = useQuery({
    queryKey: ['ndf', 'categories'],
    queryFn: () => ndfApi.categories.lire(),
  });

  const depense = useMemo<Depense | undefined>(
    () => (data?.depenses ?? []).find((d) => d.id === id),
    [data, id],
  );
  const categories = (categoriesData?.categories ?? []).filter((c) => c.actif);
  const categorie = categories.find((c) => c.id === depense?.categorie_id);
  const sousCategorie = categories.find((c) => c.id === depense?.sous_categorie_id);

  const modifier = useMutation({
    mutationFn: (body: Record<string, unknown>) => ndfApi.depenses.modifier(id, body),
    onSuccess: (res) => {
      setErreurs({});
      toast.success('Dépense enregistrée.');
      for (const m of res.forcages ?? []) toast.info(m);
      setEdition(false);
      qc.invalidateQueries({ queryKey: ['ndf', 'depenses'] });
    },
    onError: (err) => {
      setErreurs(erreursParChamp(err));
      const g = erreurGlobale(err) ?? messageSiAucunChampAffiche(err, [...CHAMPS_AFFICHES]);
      if (g) toast.error(g);
    },
  });

  const supprimer = useMutation({
    mutationFn: () => ndfApi.depenses.supprimer(id),
    onSuccess: () => {
      toast.success('Dépense supprimée.');
      qc.invalidateQueries({ queryKey: ['ndf', 'depenses'] });
      navigate('/notes-de-frais/mes-depenses');
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'La suppression a échoué.'),
  });

  if (isLoading) return <p className="text-sm text-nuit/60">Chargement…</p>;
  if (error) return <MessageErreur erreur={error} />;
  if (!depense) {
    return (
      <div className="space-y-3">
        <MessageErreur erreur={new Error('Cette dépense n’existe pas ou ne vous est pas accessible.')} />
        <BoutonSecondaire type="button" onClick={() => navigate('/notes-de-frais/mes-depenses')}>
          Retour à mes dépenses
        </BoutonSecondaire>
      </div>
    );
  }

  const statut = STATUTS[depense.statut] ?? { libelle: depense.statut, ton: 'neutre' as const };
  const modifiable = MODIFIABLES.includes(depense.statut);
  // L'administration et la RH corrigent une dépense « en vol » — y compris la
  // leur : c'est l'administrateur du module qui débloque, quelle que soit la
  // note concernée.
  const peutCorriger =
    (peutAdministrer || peutApprouverRh) &&
    ['soumise', 'approuvee_manager', 'approuvee_rh', 'en_anomalie', 'rejetee'].includes(depense.statut);
  // Les tags sont destinés au valideur : ils n'encombrent pas l'écran du salarié.
  const pourMoi = depense.alertes.filter((a) => a.niveau !== 'tag');

  return (
    <div className="space-y-4 pb-4">
      <header className="flex items-start justify-between gap-2">
        <div>
          {/* Le titre est le TYPE de dépense, pas le montant : c'est ainsi
              qu'on reconnaît une dépense. Le TTC est lu juste en dessous. */}
          <h1 className="font-titre text-lg font-bold text-nuit">
            {categorie?.libelle ?? 'Dépense'}
            {sousCategorie ? ` · ${sousCategorie.libelle}` : ''}
          </h1>
          <p className="text-sm text-nuit/60">{dateFr(depense.date_depense)}</p>
        </div>
        <BoutonSecondaire type="button" onClick={() => navigate('/notes-de-frais/mes-depenses')}>
          Retour
        </BoutonSecondaire>
      </header>

      <div className="flex flex-wrap gap-1">
        <Etiquette ton={statut.ton}>{statut.libelle}</Etiquette>
        {depense.facturable && <Etiquette ton="actif">Refacturable</Etiquette>}
        {depense.demande_remboursement && <Etiquette>Remboursement demandé</Etiquette>}
      </div>

      {pourMoi.length > 0 && (
        <section className="space-y-2">
          {pourMoi.map((a) => {
            // Une alerte de doublon DÉSIGNE l'autre dépense : sans ce bouton,
            // il faut parcourir toute la liste pour la retrouver, et comparer
            // deux montants de mémoire.
            const autre = typeof a.details?.depense_id === 'string' ? a.details.depense_id : null;
            return (
              <div
                key={a.code}
                className={`rounded-md border p-3 text-sm ${
                  a.niveau === 'bloquant'
                    ? 'border-corail-fond/60 border-l-4 border-l-corail-fond text-corail'
                    : 'border-jaune-fond/60 border-l-4 border-l-jaune-fond text-jaune'
                }`}
              >
                <p>{a.message}</p>
                {autre && (
                  <button
                    type="button"
                    onClick={() => navigate(`/notes-de-frais/comparer/${depense.id}/${autre}`)}
                    className="mt-2 min-h-11 font-semibold text-nuit underline"
                  >
                    Comparer les deux dépenses
                  </button>
                )}
              </div>
            );
          })}
        </section>
      )}

      {/* R-NDF-02, deuxième chemin d'association : depuis la dépense
          elle-même. On y pense en la relisant, pas en ouvrant une note. */}
      <RattachementNote depense={depense} />

      <section className="rounded-lg border border-nuit/10 bg-white p-4">
        <h2 className="mb-2 font-titre text-sm font-bold text-nuit">Justificatif</h2>
        {/* Le bouton ne s'affiche que s'il y a quelque chose à ouvrir : proposé
            à vide, il menait à « le justificatif n'a pas pu être chargé », qui
            laisse croire à un document perdu. */}
        {depense.a_justificatif === false ? (
          <p className="text-sm text-nuit/60">
            Aucun justificatif n’est attaché à cette dépense.
          </p>
        ) : (
          /* Aperçu DANS l'application : ouvert dans un onglet, le lecteur du
             système s'affichait sans aucun retour visible sur téléphone. */
          <div className="flex flex-wrap gap-2">
            <BoutonSecondaire type="button" onClick={() => setApercu(true)}>
              Voir le justificatif
            </BoutonSecondaire>
            {/* Pièce probante attendue par l'URSSAF, sur CHAQUE trajet. Elle
                atteste le déplacement sans exposer le calcul interne. */}
            {categorie?.est_ik && (
              <a
                href={ndfApi.depenses.urlJustificatif(depense.id, 'ik_client')}
                target="_blank"
                rel="noreferrer"
                className="flex min-h-11 items-center rounded-lg border border-nuit/20 px-4 text-sm font-medium text-nuit"
              >
                Attestation de déplacement
              </a>
            )}
          </div>
        )}
      </section>

      {!edition ? (
        <section className="space-y-2 rounded-lg border border-nuit/10 bg-white p-4 text-sm">
          <Ligne libelle="Quantité" valeur={String(depense.quantite)} />
          <Ligne libelle="Prix unitaire HT" valeur={euros(depense.prix_unitaire_ht)} />
          <Ligne libelle="TVA" valeur={euros(depense.montant_tva)} />
          <Ligne libelle="Total TTC" valeur={euros(depense.montant_ttc)} />

          {/* Dépense en devise : sans le montant d'origine et le taux, la
              conversion n'est plus justifiable des mois plus tard. */}
          {depense.devise && depense.devise !== 'EUR' && (
            <Ligne
              libelle={`Payé en ${depense.devise}`}
              valeur={`${depense.montant_original ?? '-'} ${depense.devise}${
                depense.taux_change ? ` · taux ${depense.taux_change}` : ''
              }`}
            />
          )}
          {depense.description && <Ligne libelle="Description" valeur={depense.description} />}

          {/* Refacturable : le tag seul ne dit ni à quel client ni sur quelle
              mission : c'est pourtant ce qu'on vient vérifier. */}
          {depense.facturable && (
            <div className="mt-3 space-y-2 border-t border-nuit/10 pt-3">
              <h2 className="font-titre text-sm font-bold text-nuit">Refacturable à un client</h2>
              <Ligne libelle="Client" valeur={depense.client_nom ?? 'À préciser'} />
              <Ligne libelle="Mission" valeur={depense.mission_nom ?? 'À préciser'} />
              {depense.type_mission && (
                <Ligne libelle="Type de mission" valeur={depense.type_mission} />
              )}
            </div>
          )}
        </section>
      ) : (
        <FormulaireDepense
          valeurInitiale={champsDepuis(depense)}
          categories={categories}
          erreurs={erreurs}
          enCours={modifier.isPending}
          libelleValider="Enregistrer"
          onAnnuler={() => {
            setEdition(false);
            setErreurs({});
          }}
          onSoumettre={(champs) =>
            modifier.mutate({
              ...champs,
              sous_categorie_id: champs.sous_categorie_id || null,
              description: champs.description || null,
            })
          }
        />
      )}

      {apercu && (
        <ApercuJustificatif
          url={ndfApi.depenses.urlJustificatif(depense.id)}
          onFermer={() => setApercu(false)}
        />
      )}

      {!edition && (
        <div className="flex flex-wrap gap-2">
          {modifiable ? (
            <BoutonPrincipal type="button" onClick={() => setEdition(true)}>
              Modifier
            </BoutonPrincipal>
          ) : peutCorriger ? (
            // R-VAL-03 : correction mineure en cours de contrôle. Le libellé
            // dit qui agit, parce que la modification sera tracée à son nom et
            // relisible par le salarié.
            <>
              <BoutonPrincipal type="button" onClick={() => setEdition(true)}>
                Corriger (administration)
              </BoutonPrincipal>
              <p className="w-full text-sm text-nuit/60">
                Cette dépense est en cours d’approbation. Votre correction sera enregistrée avec
                l’avant et l’après, et restera visible dans la chronologie de la note.
              </p>
            </>
          ) : (
            <p className="text-sm text-nuit/60">
              Cette dépense est partie en approbation : elle n’est plus modifiable.
            </p>
          )}
          {depense.statut === 'brouillon' &&
            (confirmation ? (
              <>
                <BoutonSecondaire type="button" onClick={() => setConfirmation(false)}>
                  Annuler
                </BoutonSecondaire>
                <button
                  type="button"
                  disabled={supprimer.isPending}
                  onClick={() => supprimer.mutate()}
                  className="min-h-11 rounded-lg bg-corail px-4 text-sm font-semibold text-white disabled:opacity-50"
                >
                  {supprimer.isPending ? 'Suppression…' : 'Confirmer la suppression'}
                </button>
              </>
            ) : (
              // Confirmation demandée : la suppression est irréversible.
              <BoutonSecondaire type="button" onClick={() => setConfirmation(true)}>
                Supprimer
              </BoutonSecondaire>
            ))}
        </div>
      )}
    </div>
  );
}

/**
 * Rattachement de la dépense à une note de frais.
 *
 * Une dépense qui n'est dans aucune note ne part pas en remboursement. Le dire
 * ICI évite de le découvrir en fin de mois, quand il faut tout reprendre.
 */
function RattachementNote({ depense }: { depense: Depense }) {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();

  const notes = useQuery({
    queryKey: ['ndf', 'notes'],
    queryFn: () => ndfApi.notes.liste(),
    retry: false,
  });

  const rafraichir = () => {
    qc.invalidateQueries({ queryKey: ['ndf', 'depenses'] });
    qc.invalidateQueries({ queryKey: ['ndf', 'notes'] });
    qc.invalidateQueries({ queryKey: ['ndf', 'notes', 'associables'] });
  };

  const associer = useMutation({
    mutationFn: (noteId: string) => ndfApi.notes.associer(noteId, [depense.id]),
    onSuccess: () => {
      toast.success('Dépense rattachée à la note.');
      rafraichir();
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'Le rattachement a échoué.'),
  });

  const creerEtAssocier = useMutation({
    mutationFn: async () => {
      const { note } = await ndfApi.notes.creer(null);
      await ndfApi.notes.associer(note.id, [depense.id]);
      return note;
    },
    onSuccess: (note) => {
      toast.success(`Note ${note.numero} créée avec cette dépense.`);
      rafraichir();
      navigate(`/notes-de-frais/note/${note.id}`);
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'La note n’a pas pu être créée.'),
  });

  const brouillons = (notes.data?.notes ?? []).filter((n) => n.statut === 'brouillon');
  const sienne = (notes.data?.notes ?? []).find((n) => n.id === depense.note_id);

  if (depense.note_id) {
    return (
      <section className="rounded-lg border border-nuit/10 bg-white p-4">
        <h2 className="mb-1 font-titre text-sm font-bold text-nuit">Note de frais</h2>
        <button
          type="button"
          onClick={() => navigate(`/notes-de-frais/note/${depense.note_id}`)}
          className="min-h-11 text-sm text-nuit underline"
        >
          {sienne ? sienne.titre || sienne.numero : 'Voir la note'}
        </button>
      </section>
    );
  }

  if (depense.statut !== 'brouillon') return null;

  return (
    <section className="rounded-lg border border-nuit/10 bg-white p-4">
      <h2 className="mb-1 font-titre text-sm font-bold text-nuit">Note de frais</h2>
      <p className="mb-2 text-sm text-nuit/70">
        Cette dépense n’est rattachée à aucune note : elle ne partira pas en remboursement tant
        qu’elle n’y est pas.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {brouillons.length > 0 && (
          <Selection
            defaultValue=""
            disabled={associer.isPending}
            onChange={(e) => e.target.value && associer.mutate(e.target.value)}
            className="max-w-xs"
          >
            <option value="">Ranger dans une note…</option>
            {brouillons.map((n) => (
              <option key={n.id} value={n.id}>
                {n.titre || n.numero}
              </option>
            ))}
          </Selection>
        )}
        <BoutonSecondaire
          type="button"
          disabled={creerEtAssocier.isPending}
          onClick={() => creerEtAssocier.mutate()}
        >
          {creerEtAssocier.isPending ? 'Création…' : 'Créer une note avec cette dépense'}
        </BoutonSecondaire>
      </div>
    </section>
  );
}

function Ligne({ libelle, valeur }: { libelle: string; valeur: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-nuit/5 pb-1 last:border-0">
      <span className="text-nuit/60">{libelle}</span>
      <span className="text-right font-medium text-nuit">{valeur}</span>
    </div>
  );
}
